/**
 * Static per-kind numbers for every entity.
 *
 * `EntityDef` is the cross-system contract from core/types; `ENTITY_TUNING`
 * holds the extra knobs a single behaviour needs and the shared def cannot
 * express. Both live here so a kind can be re-felt from one screen of numbers
 * instead of by hunting through the behaviour files.
 *
 * ## The roster is canonical
 *
 * Every walking thing in this game is a documented Backrooms entity rather than
 * an invention, and its numbers are an attempt to make the documented behaviour
 * true in play rather than to describe it in a label. Where the wikis disagree
 * the choice is recorded beside the kind it affects.
 */

import { TUNING } from '../core/config';
import { DEG, TAU } from '../core/math';
import type { EntityDef, EntityKind } from '../core/types';

const DEFS: Record<EntityKind, EntityDef> = {
  /**
   * Entity 8, "Hounds". The real threat, and the one the player already loves,
   * so its numbers are untouched.
   *
   * Blind — sightRange is 0 on purpose, not "short" — so it only ever knows
   * what it hears. Slightly faster than a sprinting player, so running is a
   * delaying tactic and stillness is the answer. (The wiki gives Hounds eyes
   * and says sustained eye contact cows them; this game made them deaf-reckoning
   * predators long before the roster was made canonical, the counterplay that
   * came out of it is verified and loved, and there is no version of "stare it
   * down" that works in a game with no aim button. Kept as it stands.)
   */
  hound: {
    kind: 'hound',
    label: 'Hound',
    walkSpeed: 1.8,
    chaseSpeed: 5.35,
    sightRange: 0,
    fov: 0,
    hearingRange: 34,
    attackRange: 1.45,
    // Roughly eighteen points a second in contact: five or six seconds from
    // full health to dead. Being caught should feel like losing, not like a
    // scratch, but there is still time to break away.
    damage: 24,
    attackCooldown: 1.35,
    health: 120,
    radius: 0.46,
    height: 1.35,
    sanityDrain: 3.2,
    lightAversion: 0,
  },

  /**
   * Entity 3, "Smilers". Barely an enemy. It stands in the dark and is looked
   * at. Almost all of its budget is in sanityDrain; the damage exists only so
   * walking into it is not free.
   */
  smiler: {
    kind: 'smiler',
    label: 'Smiler',
    walkSpeed: 0.55,
    chaseSpeed: 1.15,
    sightRange: 32,
    fov: 150 * DEG,
    hearingRange: 9,
    attackRange: 1.2,
    damage: 6,
    attackCooldown: 2.6,
    health: 40,
    radius: 0.38,
    height: 1.95,
    // Enough to comfortably out-pace the survival system's recovery while it is
    // being looked at, not enough to end a run on its own.
    sanityDrain: 3.2,
    // High enough that the director will only ever place one somewhere darker
    // than its own flinch threshold — a smiler that spawns lit is a joke.
    lightAversion: 0.7,
  },

  /**
   * Entity 5, "Clumps". Stationary hazard. Cannot be killed, only avoided —
   * which is entirely a lighting problem, since in a lit room you will never
   * touch one.
   */
  clump: {
    kind: 'clump',
    label: 'Clump',
    walkSpeed: 0,
    chaseSpeed: 0,
    sightRange: 4,
    fov: TAU,
    hearingRange: 0,
    attackRange: 1.05,
    damage: 7,
    attackCooldown: 0.6,
    health: Infinity,
    radius: 0.85,
    height: 1.1,
    sanityDrain: 0.6,
    lightAversion: 0,
  },

  /**
   * Not an entity and not in the lore, deliberately. The hollow is what a
   * failing mind puts at the end of a corridor and what the director shows
   * instead of a spawn. It is harmless and gone when confronted, and nothing in
   * the wikis has to justify it because it was never there.
   */
  hollow: {
    kind: 'hollow',
    label: 'Hollow',
    walkSpeed: 0.85,
    chaseSpeed: 0.85,
    sightRange: 40,
    fov: TAU,
    hearingRange: 0,
    attackRange: 0,
    damage: 0,
    attackCooldown: 0,
    health: 1,
    radius: 0.34,
    height: 1.78,
    sanityDrain: 1.7,
    lightAversion: 0.85,
  },

  /**
   * The Bacteria: Level 0's own, and the thing that actually runs at you.
   *
   * Tall, thin and barely humanoid — arms to the floor, short legs, no feet.
   * It moves fast and quietly, screams when it sights prey, and its documented
   * signature is that **it gets faster the longer the chase runs**. That is the
   * whole design: `chaseSpeed` starts under a sprint so the first few seconds
   * of running are genuinely an escape, and `ENTITY_TUNING.bacteria.rampPeak`
   * takes it well past one, so a chase that is still going after twenty seconds
   * is a chase the player is losing. Break line of sight early or do not break
   * it at all.
   *
   * The wikis split this one. The community wiki has no Bacteria; the Kane
   * Pixels canon has it as the series' antagonist (Hay bacillus, "the
   * Lifeform"), and the games built on that canon are where the acceleration is
   * documented. That is the version an eleven-year-old means by "the Level 0
   * monster", so that is the one built here.
   */
  bacteria: {
    kind: 'bacteria',
    label: 'Bacteria',
    // Quiet and quick even when it is only crossing a room.
    walkSpeed: 2.0,
    // Under the player's 5.0 sprint. See `rampPeak` for where it ends up.
    chaseSpeed: 4.5,
    sightRange: 34,
    fov: 150 * DEG,
    hearingRange: 32,
    attackRange: 1.5,
    damage: 22,
    attackCooldown: 1.2,
    health: 110,
    radius: 0.42,
    // Measured off the built model in `render/entbacteria.ts`, not guessed:
    // 2.26 m of wire. Canon is nearer 3.35 m, which does not fit under Level
    // 0's 3.1 m ceiling. `EntityDef.height` and the mesh are two hand-kept
    // numbers — nothing reads this one at runtime — so it says what is drawn.
    height: 2.26,
    sanityDrain: 2.6,
    // Level 0 is uniformly lit. A Bacteria that refused to enter a lit room
    // would have nowhere at all to stand.
    lightAversion: 0,
  },

  /**
   * Entity 9, "Facelings" — the Shadow Faceling variant: pitch black, faceless,
   * and much taller than a person.
   *
   * The wiki's ordinary Faceling is docile, mimics human routines and only
   * turns on someone who provokes it, which is a fine thing to read and a
   * non-event to meet. The documented Shadow variant is the one that is
   * described as agitable and drawn to light, and it is the tall dark
   * silhouette the player means. The rule it is given here — it closes only
   * while unobserved and holds absolutely still while looked at — is the
   * player's own design request rather than something the wikis say about
   * Entity 9. It is a rule the Backrooms games attach to Partygoers more often
   * than to Facelings; it is on the Faceling here because a tall featureless
   * humanoid standing perfectly still at the end of a corridor is the single
   * most frightening thing this engine can draw, and because Partygoers have a
   * better documented behaviour of their own.
   */
  faceling: {
    kind: 'faceling',
    label: 'Faceling',
    // Only ever used to close ground. It has no idle.
    walkSpeed: 2.4,
    // Fast, but it can never be seen using it, which is what makes it work.
    chaseSpeed: 4.4,
    sightRange: 36,
    // No face, no front. It is never looking the wrong way.
    fov: TAU,
    hearingRange: 28,
    attackRange: 1.5,
    damage: 20,
    attackCooldown: 1.5,
    // Nothing in this game can hurt it; the number only has to not be reachable.
    health: 140,
    radius: 0.4,
    // The built model's tallest variant (`render/entfaceling.ts` varies height
    // per instance, 2.09–2.19 m). Was 2.4, which nothing was.
    height: 2.19,
    // The most expensive thing to look at in the game, and that is the trade:
    // watching it is what keeps it still, and watching it is what costs.
    sanityDrain: 3.4,
    // Placed somewhere dim enough to read as a silhouette. It walks toward the
    // light the player is carrying from there.
    lightAversion: 0.4,
  },

  /**
   * Entity 4, "Deathmoths". Drawn to light, and therefore to whoever is
   * carrying it.
   *
   * Sized as a Praetorian — nearly a metre tall, sixty centimetres of wingspan
   * — rather than as a common male at thirty. The common ones are hand-sized
   * scavengers and would be scenery; the Praetorian is the caste the wiki
   * describes making swift attacks with paralytic legs, and it is the one worth
   * modelling.
   *
   * Its `sightRange` is the largest in the roster and that is not a mistake:
   * `perception.sightSignal` multiplies it by how lit the player is, and a lit
   * torch counts as 0.9 regardless of the room. So with the flashlight on it
   * sees the player from nearly forty metres, and in a dark room with the torch
   * off it sees eight. Turning the light off is the counterplay, and it is the
   * only entity that teaches the flashlight has a cost.
   */
  deathmoth: {
    kind: 'deathmoth',
    label: 'Deathmoth',
    walkSpeed: 1.6,
    chaseSpeed: 5.0,
    sightRange: 42,
    // Compound eyes. It does not have to be facing you.
    fov: 300 * DEG,
    // Moths do not hunt by ear. This is nearly deaf on purpose: crouching does
    // not hide you from one, switching the torch off does.
    hearingRange: 8,
    attackRange: 1.3,
    // Chip damage on a fast clock — about nine a second, half a hound. It is
    // the reliable, survivable attacker, and the one a player can actually beat
    // by understanding it.
    damage: 7,
    attackCooldown: 0.75,
    health: 30,
    // Its half-span, not its body. The model is 1.35 m wingtip to wingtip, and
    // a radius that only covered the thorax put the wings through walls.
    radius: 0.62,
    height: 1.15,
    sanityDrain: 1.4,
    // The opposite of light-shy. It is what the light is for.
    lightAversion: 0,
  },

  /**
   * Entity 67, "Partygoers". The Funrooms' hosts, and they have been waiting.
   *
   * Tall (the wiki gives 6.3–7.7 ft; 2.1 m is the middle), yellow, with a
   * smiley face smeared across the head and arms that end in mouths. Two things
   * are agreed everywhere and both are built here: they hunt in packs — "you
   * can never catch a Partygoer alone" — and the cheerfulness is a lure rather
   * than a mood.
   *
   * The wikis disagree on speed. The community wiki calls them slow and
   * nearsighted; every Backrooms *game* makes them fast and lethal. Split: the
   * nearsightedness is kept, because it is the counterplay and it is the more
   * interesting of the two claims — `sightRange` is the shortest of any hunter,
   * so at range they genuinely cannot find you — and the speed is taken from
   * the games, because a Partygoer that notices you and then ambles over is not
   * a threat, and the player asked for things that come at you.
   */
  partygoer: {
    kind: 'partygoer',
    label: 'Partygoer',
    // The approach, while it is still pretending. Slower than a walking player.
    walkSpeed: 1.9,
    // The moment it stops pretending.
    chaseSpeed: 5.2,
    // Nearsighted. Keep your distance and it cannot see you at all.
    sightRange: 14,
    fov: 120 * DEG,
    // But it hears the party arriving from right across the floor.
    hearingRange: 34,
    attackRange: 1.5,
    damage: 21,
    attackCooldown: 1.3,
    health: 95,
    radius: 0.42,
    height: 2.1,
    // A thing walking toward you laughing is worse than a thing running at you.
    sanityDrain: 3.0,
    // The Funrooms are floodlit and it lives there.
    lightAversion: 0,
  },

  /**
   * The Sticky-Centipede. Yellow, red-striped, far too many legs, and at home
   * in water.
   *
   * The wiki describes an ambush predator: it lies motionless in a narrow gap
   * or under debris for long stretches, has four small dark eyes and
   * correspondingly poor sight, and coats itself in an adhesive that it uses to
   * hold prey. So it is nearly blind and hears everything — the longest ears in
   * the roster, because footfalls in standing water are the loudest thing a
   * player can produce (`player.noiseWaterBonus`) and this is the entity that
   * turns that into a threat.
   *
   * Water is its whole counterplay. In it, it out-runs a sprint; out of it, a
   * sprinting player is faster. See `ENTITY_TUNING.centipede.waterScale`.
   */
  centipede: {
    kind: 'centipede',
    label: 'Sticky-Centipede',
    walkSpeed: 1.5,
    // On dry floor. `waterScale` takes it past a sprint in the wet.
    chaseSpeed: 4.4,
    // Four small dark eyes. It effectively does not see.
    sightRange: 9,
    fov: 200 * DEG,
    // It feels the floor. This is the longest hearing range in the game.
    hearingRange: 38,
    attackRange: 1.5,
    damage: 16,
    attackCooldown: 1.0,
    health: 80,
    radius: 0.5,
    // Low and long. It is genuinely easy to miss lying still in shallow water.
    height: 0.75,
    sanityDrain: 1.8,
    lightAversion: 0.1,
  },
};

/** The full def table, keyed by kind. */
export const ENTITY_DEFS: Readonly<Record<EntityKind, EntityDef>> = DEFS;

/** Static numbers for one kind. */
export function getEntityDef(kind: EntityKind): EntityDef {
  return DEFS[kind];
}

/**
 * The kinds that earn a pursuit licence — the ones that will actually arrive.
 *
 * Derived from the roster in `TUNING.entities.pursuit.kinds` rather than
 * written out again, so there is exactly one list and a kind cannot be a hunter
 * in one file and not in another. Documented at length beside that roster.
 */
const PURSUERS: ReadonlySet<EntityKind> = new Set<EntityKind>(TUNING.entities.pursuit.kinds);

/** True when this kind is allowed to keep coming from beyond `activeRadius`. */
export function pursues(kind: EntityKind): boolean {
  return PURSUERS.has(kind);
}

/**
 * Behaviour-local tuning. Kept beside the defs rather than inside each
 * behaviour file so all of an entity's feel is readable in one place.
 */
export const ENTITY_TUNING = {
  hound: {
    /** Seconds of growling before it commits. This is the player's warning. */
    growlLead: 1.6,
    /**
     * The first hound of a run gets more than double that.
     *
     * Nothing in this game teaches the counterplay before it kills you, and the
     * counterplay is counter-intuitive: you have to stop, in the dark, while
     * something is coming. A first-time player hears one growl, panics, sprints
     * — the loudest thing they can do — and is dead in five seconds without ever
     * having seen what did it. That is not difficulty, it is a missing lesson.
     *
     * So the first one is slow to commit and loud about being slow. Every hound
     * after it in the run runs at `growlLead`, and its teeth are untouched: a
     * hound that reaches you should still very nearly finish you.
     */
    firstGrowlLead: 3.6,
    /**
     * Seconds between growls during the wind-up. A single 1.8-second one-shot in
     * a reverberant corridor is very hard to place; a pulse train is both
     * locatable and legible as a countdown that is running out.
     */
    growlPulse: 0.95,
    /** Minimum seconds committed to a chase, so it cannot flicker in and out. */
    chaseCommit: 2.4,
    /** Awareness needed to wind up and to keep running. */
    commitAwareness: 0.6,
    dropAwareness: 0.16,
    /** Awareness gained per second per unit of heard noise. */
    hearGain: 2.3,
    /** Awareness lost per second of silence. */
    forget: 0.34,
    /** Seconds between running footfalls. */
    stepInterval: 0.32,
    /** Seconds between idle sniffs and grumbles while wandering. */
    idleInterval: [7, 18] as const,
  },

  smiler: {
    /** Metres. Any closer and it backs away rather than be reached. */
    personalSpace: 6.5,
    /** Light level (0..1) on it that counts as being caught in the beam. */
    lightFlinch: 0.42,
    /** Metres. Past this it stops caring about being watched. */
    stareRange: 32,
    /** Seconds of being lit before it withdraws entirely and despawns. */
    fleePatience: 4.5,
    /** Seconds between breaths while it stares. */
    breathInterval: [3.5, 8] as const,
  },

  clump: {
    /** Metres. Sanity prickles this far out, before contact. */
    dreadRange: 3.5,
  },

  hollow: {
    /** Direct, deliberate looks before it is gone. */
    looksToVanish: 2,
    /** Cosine of the half-angle that counts as looking *directly* at it. */
    lookCos: Math.cos(13 * DEG),
    /** Seconds it lasts if never confronted. */
    lifetime: [18, 34] as const,
  },

  bacteria: {
    /**
     * The acceleration, which is the entity.
     *
     * Chase speed is multiplied from 1 up to `rampPeak` over `rampTime` seconds
     * of *continuous* chasing, and the multiplier decays back at `rampCool` a
     * second once it is not. Against a 5.0 m/s sprint: 4.5 m/s for the first
     * few seconds, level at about eight, and 6.4 m/s by twenty-two. So the
     * first corner is worth taking and the fifth is not.
     */
    rampTime: 22,
    rampPeak: 1.42,
    rampCool: 0.09,
    /** Awareness at which it screams and commits. It does not creep up on you. */
    commitAwareness: 0.4,
    /** Awareness below which it finally loses the thread. */
    dropAwareness: 0.1,
    /** Awareness per second per unit of signal, and lost per second without one. */
    gain: 2.6,
    forget: 0.2,
    /** Seconds between screams while it is running. */
    screamInterval: 4.5,
    /** Seconds it will keep running with no signal at all before giving up. */
    chaseCommit: 5,
  },

  faceling: {
    /**
     * Metres. Being looked at from further than this does not hold it — past
     * the fog it is not being observed in any sense the player would recognise,
     * and a freeze the player cannot see is a freeze that only slows the game
     * down. Sits just past `LevelSpec.fogFar` on the murkiest level.
     */
    observeRange: 36,
    /**
     * Seconds after losing sight of the player before it stops closing and
     * starts casting about. Long: it does not lose people.
     */
    patience: 30,
    /** Awareness gained per second of signal, and lost per second without one. */
    gain: 1.8,
    forget: 0.05,
  },

  deathmoth: {
    /**
     * Light on the player (0..1, the torch counts as 0.9) below which it loses
     * interest entirely and goes back to circling a ceiling fixture. This is
     * the switch the player is meant to find.
     */
    lightGate: 0.3,
    /** Awareness per second per unit of signal, and lost per second without one. */
    gain: 2.2,
    forget: 0.55,
    /** Awareness at which it commits. Low — it is not cautious, it is a moth. */
    commitAwareness: 0.3,
    /**
     * The weave. It does not fly in a straight line, so a chase is survivable
     * at a speed that would otherwise be unfair: metres of lateral offset, and
     * radians per second the offset rotates through.
     */
    weaveAmplitude: 2.2,
    weaveRate: 2.4,
    /** Metres. Radius it circles a fixture in while it has nothing better to do. */
    lampRadius: 6,
    /** Seconds between wingbeat bursts while flying. */
    wingInterval: [0.7, 1.1] as const,
  },

  partygoer: {
    /**
     * Metres. Inside this the act drops: it screams, it calls the others, and
     * it runs. Outside it, it walks toward the player laughing. Set at about
     * one long room, so the reveal lands while the player can see it happen.
     */
    revealRange: 12,
    /** Awareness at which it starts the approach at all. */
    noticeAwareness: 0.35,
    /** Awareness per second per unit of signal, and lost per second without one. */
    gain: 2.0,
    forget: 0.22,
    /**
     * Metres. When one drops the act it tells every other partygoer on the
     * floor where the player is, at this loudness. Deliberately unlimited in
     * range: "you can never catch a Partygoer alone" is the one thing every
     * source agrees on, and a pack that has to be within earshot of each other
     * is not a pack.
     */
    callLoudness: 0.9,
    /** Seconds between laughs during the approach, and once it is running. */
    laughInterval: [2.5, 5.5] as const,
    chaseLaughInterval: [1.6, 2.8] as const,
    /** Seconds it keeps running after the signal is gone. They are committed. */
    chaseCommit: 8,
    /** Awareness below which even that runs out. */
    dropAwareness: 0.12,
  },

  centipede: {
    /**
     * Multiplier on its speed while the cell under it is standing water. Above
     * 1 in the wet and 1 on dry ground, so `chaseSpeed * waterScale` = 5.9 m/s
     * beats a 5.0 m/s sprint and `chaseSpeed` alone loses to one. Getting out
     * of the water is the answer, and it is an answer the player can see.
     */
    waterScale: 1.35,
    /**
     * Metres. Inside this, with a clear line, it stops feeling its way and
     * lunges. It is an ambush predator: the approach is slow and the last nine
     * metres are not.
     */
    lungeRange: 9,
    /** Seconds the lunge itself is worth, as a further multiplier on speed. */
    lungeTime: 2.2,
    lungeScale: 1.3,
    /**
     * Seconds it lies still before an approach, once it has a direction. The
     * wiki's word is that it can hold motionless for prolonged periods; in play
     * it is the beat that makes the lunge read as an ambush rather than as a
     * charge.
     */
    settleTime: [2.5, 5] as const,
    /** Awareness per second per unit of signal, and lost per second without one. */
    gain: 2.4,
    /** Very low. The adhesive is the metaphor: what it has, it keeps. */
    forget: 0.07,
    /** Awareness at which it commits. */
    commitAwareness: 0.4,
  },
};
